import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircleCheck,
  faCircleXmark,
  faClock,
} from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import type {
  ThreadInteraction,
  ThreadMessage,
  ThreadView,
} from "@/services/api/agentic-v2/types";
import { AgentMessageHtml } from "@/components/missions/AgentMessageHtml";
import { ThreadArtifactCard } from "@/components/missions/task-thread/ThreadArtifactCard";
import { LazyToolCallStepDetail } from "@/components/missions/task-thread/LazyToolCallStepDetail";
import { prettifyToolName } from "@/components/missions/task-thread/prettifyToolName";
import { ThreadTimestamp } from "@/components/missions/task-thread/ThreadTimestamp";
import {
  collapseThreadTimestamps,
  threadItemTimestampIso,
  type ThreadTimestampDisplay,
} from "@/components/missions/task-thread/threadTimestampUtils";
import { Tooltip } from "@/components/ui/tooltip/Tooltip";
import WorkerAvatar from "@/components/worker/WorkerAvatar";

type ConversationItem =
  | { kind: "message"; key: string; message: ThreadMessage }
  | {
      kind: "step";
      key: string;
      interactionId: string;
      stepIndex: number;
      step: ThreadInteraction["steps"][number];
    };

export function buildConversationItems(
  messages: ThreadMessage[],
  interactions: ThreadInteraction[]
): ConversationItem[] {
  const byInteraction = new Map<string, ThreadMessage[]>();
  const orphanMessages: ThreadMessage[] = [];

  for (const m of messages) {
    if (m.interaction_id) {
      const bucket = byInteraction.get(m.interaction_id);
      if (bucket) bucket.push(m);
      else byInteraction.set(m.interaction_id, [m]);
    } else {
      orphanMessages.push(m);
    }
  }

  type Entry = { ts: number; item: ConversationItem };
  const entries: Entry[] = [];

  for (const interaction of interactions) {
    const interactionMessages = byInteraction.get(interaction._id) ?? [];
    const interactionStart = new Date(interaction.started_at).getTime();
    const localEntries: Entry[] = [];

    interactionMessages.forEach((m) => {
      localEntries.push({
        ts: new Date(m.created_at).getTime(),
        item: { kind: "message", key: m._id, message: m },
      });
    });

    interaction.steps.forEach((step, i) => {
      if (step.type === "reasoning") return;
      const ts = step.timestamp
        ? new Date(step.timestamp).getTime()
        : interactionStart + i;
      localEntries.push({
        ts,
        item: {
          kind: "step",
          key: `${interaction._id}:step:${i}`,
          interactionId: interaction._id,
          stepIndex: i,
          step,
        },
      });
    });

    localEntries.sort((a, b) => a.ts - b.ts);
    entries.push(...localEntries);
  }

  for (const m of orphanMessages) {
    entries.push({
      ts: new Date(m.created_at).getTime(),
      item: { kind: "message", key: m._id, message: m },
    });
  }

  entries.sort((a, b) => a.ts - b.ts);
  return entries.map((e) => e.item);
}

function iconForLabel(label: string | null | undefined) {
  if (!label) return faClock;
  const l = label.toLowerCase();
  if (l.includes("complet") || l.includes("done")) return faCircleCheck;
  if (l.includes("block") || l.includes("error")) return faCircleXmark;
  return faClock;
}

function ConversationMessageRow({
  message,
  timestampDisplay,
}: {
  message: ThreadMessage;
  timestampDisplay: ThreadTimestampDisplay | null;
}) {
  if (message.sender_type === "system" || message.content_type === "system_event") {
    const sysContent = message.content as { label?: string; note?: string };
    const label = sysContent.label ?? "System event";
    return (
      <div className="text-xs text-slate-400 py-0.5">
        <div className="flex items-center gap-2">
          <FontAwesomeIcon icon={iconForLabel(label)} className="text-[10px]" />
          <span>{label}</span>
          {timestampDisplay ? (
            <ThreadTimestamp
              display={timestampDisplay}
              className="ml-auto text-slate-500"
            />
          ) : null}
        </div>
        {sysContent.note ? (
          <p className="pl-4 mt-0.5 text-slate-300 italic">
            “{sysContent.note}”
          </p>
        ) : null}
      </div>
    );
  }

  if (message.sender_type === "user") {
    const userContent = message.content as { text?: string };
    return (
      <div className="flex justify-end">
        <div className="rounded-xl px-3 py-2 text-sm bg-slate-700/60 text-slate-100 max-w-[80%]">
          <p className="leading-relaxed">{userContent.text ?? ""}</p>
          {timestampDisplay ? (
            <p className="text-[10px] text-slate-400 mt-1 text-right">
              <ThreadTimestamp display={timestampDisplay} />
            </p>
          ) : null}
        </div>
      </div>
    );
  }

  if (message.content_type === "artifact_card") {
    return (
      <ThreadArtifactCard message={message} timestampDisplay={timestampDisplay} />
    );
  }

  const content = message.content as {
    text?: string;
    agent_name?: string | null;
  };
  const text = content.text ?? "";

  return (
    <div className="rounded-xl rounded-br-md px-3 py-2 text-sm bg-indigo-500/[0.08] text-slate-200 min-w-0 w-full max-w-full overflow-hidden">
      <div className="flex items-center justify-between text-[11px] mb-0.5">
        <div className="flex items-center gap-1.5">
          <WorkerAvatar seed={content.agent_name ?? "Agent"} size={18} user className="shrink-0" />
          <span className="text-slate-300 font-medium">{content.agent_name ?? "Agent"}</span>
        </div>
        {timestampDisplay ? (
          <ThreadTimestamp display={timestampDisplay} className="text-slate-400" />
        ) : null}
      </div>
      <AgentMessageHtml html={text} />
    </div>
  );
}

function ConversationStepRow({
  interactionId,
  stepIndex,
  step,
  timestampDisplay,
}: {
  interactionId: string;
  stepIndex: number;
  step: ThreadInteraction["steps"][number];
  timestampDisplay: ThreadTimestampDisplay | null;
}) {
  if (step.type === "tool_call") {
    const label = step.tool_display_name || prettifyToolName(step.tool);
    return (
      <div className="flex items-center gap-2 text-xs text-slate-400 py-0.5">
        <FontAwesomeIcon icon={faCircleCheck} className="text-[10px] shrink-0" />
        <span className="min-w-0">
          Used{" "}
          <Tooltip
            placement="top"
            maxWidth="min(24rem, 92vw)"
            content={
              <LazyToolCallStepDetail interactionId={interactionId} stepIndex={stepIndex} />
            }
          >
            <span className="text-slate-300 underline decoration-dotted decoration-slate-600 underline-offset-2 cursor-help">
              {label}
            </span>
          </Tooltip>
        </span>
        {timestampDisplay ? (
          <ThreadTimestamp
            display={timestampDisplay}
            className="ml-auto shrink-0 text-slate-500"
          />
        ) : null}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 text-xs text-slate-400 py-0.5">
      <FontAwesomeIcon icon={faClock} className="text-[10px] shrink-0" />
      <span className="min-w-0">{step.content ?? step.type}</span>
      {timestampDisplay ? (
        <ThreadTimestamp
          display={timestampDisplay}
          className="ml-auto shrink-0 text-slate-500"
        />
      ) : null}
    </div>
  );
}

export interface ConversationBubblesProps {
  thread: ThreadView;
}

export function ConversationBubbles({ thread }: ConversationBubblesProps) {
  const items = buildConversationItems(thread.messages ?? [], thread.interactions ?? []);
  const timestampDisplays = collapseThreadTimestamps(items.map(threadItemTimestampIso));

  if (items.length === 0) {
    return <p className="text-sm text-slate-500">No interactions yet.</p>;
  }

  return (
    <div className="space-y-1.5" data-testid="conversation-bubbles">
      {items.map((item, index) => {
        const timestampDisplay = timestampDisplays[index] ?? null;
        if (item.kind === "step") {
          return (
            <ConversationStepRow
              key={item.key}
              interactionId={item.interactionId}
              stepIndex={item.stepIndex}
              step={item.step}
              timestampDisplay={timestampDisplay}
            />
          );
        }
        return (
          <ConversationMessageRow
            key={item.key}
            message={item.message}
            timestampDisplay={timestampDisplay}
          />
        );
      })}
    </div>
  );
}
